import React from "react";
import {
  Grid,
  Box,
  Typography,
  Container,
  Card,
  CardContent,
} from "@mui/material";
import {
  Code,
  Smartphone,
  Palette,
  Cloud,
  TrendingUp,
  Settings,
  ShoppingCart,
  Share2,
  Zap,
  Database,
  CreditCard,
  HeadphonesIcon,
} from "lucide-react";
import Navbar from "../components/FrontPages/Common/Navbar";
import Footer from "../components/FrontPages/Common/Footer";
import PageBanner from "../components/FrontPages/Common/PageBanner";
import Cta from "../components/FrontPages/Common/Cta";

const Services = () => {
  // Main Services Data
  const mainServices = [
    {
      title: "AI Integration",
      description:
        "Transform your business with intelligent automation, predictive analytics, and machine learning solutions that adapt to your needs and drive innovation.",
      icon: <Zap size={40} />,
      backgroundColor: "#DDE4FF",
      features: [
        "Machine Learning Models",
        "Predictive Analytics",
        "Intelligent Automation",
        "Natural Language Processing",
      ],
    },
    {
      title: "ERPNext Development",
      description:
        "Custom ERPNext solutions built on the Frappe framework. From implementation to advanced customization, we help streamline your business operations with this powerful open-source ERP.",
      icon: <Database size={40} />,
      backgroundColor: "#E8F5E8",
      features: [
        "Custom DocType & Module Development",
        "Frappe Framework Customization",
        "Data Migration & Import",
        "API Integration & Webhooks",
        "Custom Reports & Dashboards",
        "Training & Support",
      ],
    },
    {
      title: "Odoo Development",
      description:
        "Expert Odoo development services leveraging Python and PostgreSQL. Complete business management solutions with custom modules, integrations, and enterprise-grade implementations.",
      icon: <Settings size={40} />,
      backgroundColor: "#FFF0E6",
      features: [
        "Custom Module Development",
        "Third-party System Integration",
        "Workflow Automation & Actions",
        "Multi-company & Multi-currency Setup",
        "Custom Views & Reports",
        "Migration & Deployment",
      ],
    },
    {
      title: "Mobile App Development",
      description:
        "Native and cross-platform mobile applications that deliver exceptional user experiences across iOS and Android platforms.",
      icon: <Smartphone size={40} />,
      backgroundColor: "#F3E8FF",
      features: [
        "iOS & Android Apps",
        "Cross-platform Solutions",
        "App Store Optimization",
        "Maintenance & Updates",
      ],
    },
    {
      title: "Web Development",
      description:
        "Responsive, scalable web applications built with modern technologies to meet your business objectives and user expectations.",
      icon: <Code size={40} />,
      backgroundColor: "#E6F7FF",
      features: [
        "Custom Web Apps",
        "Progressive Web Apps",
        "API Development",
        "Performance Optimization",
      ],
    },
    {
      title: "E-commerce Solutions",
      description:
        "Complete e-commerce platforms with secure payment gateways, inventory management, and customer engagement tools.",
      icon: <ShoppingCart size={40} />,
      backgroundColor: "#FFE8D4",
      features: [
        "Online Store Development",
        "Payment Gateway Integration",
        "Inventory Management",
        "Mobile Commerce",
      ],
    },
    {
      title: "Graphics Design",
      description:
        "Creative visual solutions including branding, marketing materials, and digital assets that make your business stand out.",
      icon: <Palette size={40} />,
      backgroundColor: "#F0F8FF",
      features: [
        "Brand Identity",
        "Marketing Materials",
        "Digital Graphics",
        "Print Design",
      ],
    },
    {
      title: "Hosting Services",
      description:
        "Reliable, secure, and scalable hosting solutions with 99.9% uptime guarantee and 24/7 technical support.",
      icon: <Cloud size={40} />,
      backgroundColor: "#F5F5F5",
      features: [
        "Web Hosting",
        "Cloud Hosting",
        "Domain Registration",
        "SSL Certificates",
      ],
    },
    {
      title: "Digital Marketing",
      description:
        "Comprehensive digital marketing strategies to boost your online presence and drive business growth.",
      icon: <TrendingUp size={40} />,
      backgroundColor: "#E8F5E8",
      features: [
        "SEO Optimization",
        "Social Media Marketing",
        "Content Marketing",
        "PPC Campaigns",
      ],
    },
    {
      title: "Social Media Branding",
      description:
        "Build a strong social media presence with engaging content, brand consistency, and community management.",
      icon: <Share2 size={40} />,
      backgroundColor: "#FFE8F1",
      features: [
        "Brand Strategy",
        "Content Creation",
        "Community Management",
        "Social Media Analytics",
      ],
    },
    {
      title: "Payment Integration",
      description:
        "Secure payment solutions including M-PESA, PayPal, and other global payment gateways for seamless transactions.",
      icon: <CreditCard size={40} />,
      backgroundColor: "#F3E8FF",
      features: [
        "M-PESA Integration",
        "PayPal Integration",
        "Stripe Integration",
        "Custom Payment Solutions",
      ],
    },
    {
      title: "24/7 Support",
      description:
        "Round-the-clock technical support and maintenance services to ensure your systems run smoothly at all times.",
      icon: <HeadphonesIcon size={40} />,
      backgroundColor: "#FFE8D4",
      features: [
        "Technical Support",
        "System Maintenance",
        "Bug Fixes",
        "Performance Monitoring",
      ],
    },
  ];

  return (
    <>
      <div className="fp-wrapper">
        <Navbar />
        <PageBanner pageTitle='Our Services' />

        <Container maxWidth="xl">
          {/* Hero Section */}

          <Box textAlign="center" mb={6}>
            <Box className="section-title text-center">
              <div className="sub-title">
                <span className="text-purple">Our Services</span>
              </div>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "24px", md: "28px", lg: "34px", xl: "36px" },
                }}
              >
                The services we offer
              </Typography>
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "16px", md: "20px", lg: "24px" },
                color: "text.secondary",
                maxWidth: "800px",
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              Comprehensive digital solutions to power your business growth
              through intelligent technology and expert craftsmanship
            </Typography>
          </Box>

          {/* Services Grid */}
          <Grid container spacing={4}marginBottom={6}>
            {mainServices.map((service, index) => (
              <Grid key={index} item xs={12} sm={6} lg={4}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    },
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <CardContent sx={{ p: 4, height: "100%" }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        backgroundColor: service.backgroundColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        color: "#666",
                      }}
                    >
                      {service.icon}
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        mb: 2,
                        color: "text.primary",
                      }}
                    >
                      {service.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mb: 3,
                        lineHeight: 1.6,
                      }}
                    >
                      {service.description}
                    </Typography>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: "bold",
                          mb: 1,
                          color: "text.primary",
                        }}
                      >
                        Key Features:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {service.features.map((feature, idx) => (
                          <Typography
                            key={idx}
                            component="li"
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              mb: 0.5,
                              fontSize: "14px",
                            }}
                          >
                            {feature}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

         {/* Cta Section */}
         <Cta />
        </Container>

        <Footer />
      </div>
    </>
  );
};

export default Services;
